import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'

export default function ClinicCompliance() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('name')

  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const fromDate = thirtyDaysAgo.toISOString().split('T')[0]

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    const { data: patientList } = await supabase
      .from('users')
      .select('*')
      .eq('clinic_id', profile.clinic_id)
      .eq('role', 'patient')
      .order('full_name')

    if (!patientList || patientList.length === 0) {
      setPatients([])
      setLoading(false)
      return
    }

    const enriched = await Promise.all(patientList.map(async patient => {
      const now = new Date()

      const [
        { data: assignments },
        { data: interventionAssignments },
        { data: completions },
        { data: interventionCompletions }
      ] = await Promise.all([
        supabase.from('assignments').select('*, exercises(*)').eq('patient_id', patient.id),
        supabase.from('intervention_assignments').select('*, interventions(*)').eq('patient_id', patient.id),
        supabase.from('completions').select('*').eq('patient_id', patient.id).gte('completed_on', fromDate),
        supabase.from('intervention_completions').select('*').eq('patient_id', patient.id).gte('completed_on', fromDate)
      ])

      const activeAssignments = (assignments || []).filter(a => {
        if (!a.num_weeks || !a.assigned_at) return true
        const end = new Date(a.assigned_at)
        end.setDate(end.getDate() + a.num_weeks * 7)
        return now <= end
      })

      const activeInterventions = (interventionAssignments || []).filter(a => {
        if (!a.num_weeks || !a.assigned_at) return true
        const end = new Date(a.assigned_at)
        end.setDate(end.getDate() + a.num_weeks * 7)
        return now <= end
      })

      const totalActive = activeAssignments.length + activeInterventions.length
      if (totalActive === 0) return null

      const todayEx = (completions || []).filter(c => c.completed_on === today).length
      const todayIv = (interventionCompletions || []).filter(c => c.completed_on === today).length
      const todayTotal = todayEx + todayIv
      const todayPossible = activeAssignments.length + activeInterventions.length

      const thirtyEx = (completions || []).length
      const thirtyIv = (interventionCompletions || []).length
      const thirtyRate = totalActive > 0
        ? Math.round(((thirtyEx + thirtyIv) / (totalActive * 30)) * 100)
        : 0

      return {
        ...patient,
        totalActive,
        todayTotal,
        todayPossible,
        thirtyRate
      }
    }))

    setPatients(enriched.filter(Boolean))
    setLoading(false)
  }

  function sorted() {
    return [...patients].sort((a, b) => {
      if (sortBy === 'name') return a.full_name.localeCompare(b.full_name)
      if (sortBy === 'today') return b.todayTotal - a.todayTotal
      if (sortBy === 'rate') return a.thirtyRate - b.thirtyRate
      return 0
    })
  }

  function rateColor(rate) {
    if (rate >= 80) return '#15803d'
    if (rate >= 50) return '#d97706'
    return '#dc2626'
  }

  function rateBg(rate) {
    if (rate >= 80) return '#f0fdf4'
    if (rate >= 50) return '#fffbeb'
    return '#fef2f2'
  }

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician')} className="back-link" style={{ cursor: 'pointer' }}>← Dashboard</span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1>Clinic compliance</h1>
            <p>{patients.length} patient{patients.length !== 1 ? 's' : ''} with active programs</p>
          </div>
        </div>

        {patients.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <h2 style={{ marginBottom: 8 }}>No active programs</h2>
            <p>Patients with active assignments will appear here</p>
          </div>
        )}

        {patients.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, alignSelf: 'center' }}>Sort by:</p>
              {[
                { key: 'name', label: 'Name' },
                { key: 'today', label: 'Today' },
                { key: 'rate', label: '30-day rate ↑' }
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortBy(opt.key)}
                  style={{
                    padding: '6px 12px', fontSize: 13,
                    background: sortBy === opt.key ? 'var(--brand)' : 'var(--surface)',
                    color: sortBy === opt.key ? 'white' : 'var(--text-primary)',
                    border: sortBy === opt.key ? 'none' : '1.5px solid var(--border)',
                    borderRadius: 8
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sorted().map(patient => (
                <div
                  key={patient.id}
                  className="card card-hover"
                  onClick={() => navigate('/clinician/patients/' + patient.id + '/compliance')}
                  style={{ display: 'flex', alignItems: 'center', gap: 14 }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--brand-light)', color: 'var(--brand)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 15
                  }}>
                    {patient.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15, margin: '0 0 2px' }}>
                      {patient.full_name}
                    </p>
                    <p style={{ fontSize: 12, margin: 0 }}>
                      {patient.totalActive} active item{patient.totalActive !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Today</p>
                      <p style={{ fontWeight: 700, fontSize: 16, color: patient.todayTotal === patient.todayPossible ? '#15803d' : 'var(--text-primary)', margin: 0 }}>
                        {patient.todayTotal}<span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 400 }}>/{patient.todayPossible}</span>
                      </p>
                    </div>

                    <div style={{
                      padding: '6px 12px', borderRadius: 99, textAlign: 'center',
                      background: rateBg(patient.thirtyRate)
                    }}>
                      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '0 0 1px', textTransform: 'uppercase', letterSpacing: 0.5 }}>30d</p>
                      <p style={{ fontWeight: 700, fontSize: 16, color: rateColor(patient.thirtyRate), margin: 0 }}>
                        {patient.thirtyRate}%
                      </p>
                    </div>

                    <span style={{ color: 'var(--brand)', fontSize: 18 }}>→</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  )
}
